import React, { useState, useRef } from 'react';
import { getPinataService, MAX_VIDEO_DURATION } from '../services/PinataService';
import './MediaUpload.css';

/**
 * MediaUpload Component - Handles file uploads to Pinata IPFS
 * Supports images and video clips (max 140s) with preview, storage quota, and upgrade flow
 */
const MediaUpload = ({ 
  onMediaUploaded, 
  onUploadStart, 
  onUploadProgress, 
  onUploadError,
  walletAddress,
  disabled = false,
  maxFiles = 1,
  showPreview = true 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [videoDurationError, setVideoDurationError] = useState('');
  const fileInputRef = useRef(null);

  const pinata = getPinataService();

  // Storage info for current wallet
  const storageInfo = walletAddress ? pinata.getStorageInfo(walletAddress) : null;
  const usedPct = storageInfo ? Math.min(100, Math.round((storageInfo.used / storageInfo.limit) * 100)) : 0;

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setVideoDurationError('');
    const filesToProcess = files.slice(0, maxFiles);
    const validFiles = [];
    const errors = [];

    for (const file of filesToProcess) {
      const validation = pinata.validateMedia(file);
      if (!validation.valid) {
        errors.push(`${file.name}: ${validation.error}`);
        continue;
      }

      // Check video duration client-side
      if (file.type.startsWith('video/')) {
        const durCheck = await pinata.checkVideoDuration(file);
        if (!durCheck.valid) {
          setVideoDurationError(durCheck.error);
          errors.push(`${file.name}: ${durCheck.error}`);
          continue;
        }
      }

      validFiles.push(file);
    }

    if (errors.length > 0) {
      onUploadError && onUploadError(errors.join('\n'));
      if (validFiles.length === 0) return;
    }

    setSelectedFiles(validFiles);
    if (showPreview) generatePreviews(validFiles);
  };

  const generatePreviews = (files) => {
    const newPreviews = [];
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews[index] = {
          file,
          url: e.target.result,
          type: file.type.startsWith('video/') ? 'video' : 'image'
        };
        if (newPreviews.filter(p => p).length === files.length) {
          setPreviews([...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0 || !walletAddress) return;

    setUploading(true);
    setUploadProgress(0);
    onUploadStart && onUploadStart();

    try {
      const uploadResults = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        console.log(`📤 Uploading ${file.name} to Pinata IPFS...`);

        const result = await pinata.uploadMedia(file, walletAddress);

        if (result.success) {
          uploadResults.push(result);
          const progress = Math.round(((i + 1) / selectedFiles.length) * 100);
          setUploadProgress(progress);
          onUploadProgress && onUploadProgress(progress);
        } else if (result.error === 'STORAGE_EXCEEDED') {
          // Show upgrade modal
          setUpgradeInfo(result);
          setShowUpgradeModal(true);
          throw new Error(result.message);
        } else {
          throw new Error(`Failed to upload ${file.name}: ${result.error}`);
        }
      }

      console.log('✅ All files uploaded to IPFS!', uploadResults);
      onMediaUploaded && onMediaUploaded(uploadResults);

      setSelectedFiles([]);
      setPreviews([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('❌ Upload failed:', error);
      onUploadError && onUploadError(error.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setSelectedFiles([]);
    setPreviews([]);
    setVideoDurationError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isReady = pinata.isAvailable();

  return (
    <div className="media-upload">
      {/* Storage Quota Bar */}
      {walletAddress && storageInfo && (
        <div className="storage-quota">
          <div className="storage-quota-header">
            <span className="storage-label">
              📦 {storageInfo.tierName} Plan — {formatBytes(storageInfo.used)} / {storageInfo.tierLabel}
            </span>
            {usedPct >= 80 && (
              <button
                className="upgrade-link"
                onClick={() => {
                  setUpgradeInfo({ upgradeTiers: pinata.getUpgradeTiers(walletAddress), storageInfo });
                  setShowUpgradeModal(true);
                }}
              >
                Upgrade
              </button>
            )}
          </div>
          <div className="storage-bar">
            <div
              className={`storage-bar-fill ${usedPct >= 90 ? 'critical' : usedPct >= 70 ? 'warning' : ''}`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Status */}
      <div className="upload-status">
        {isReady ? (
          <div className="status-indicator connected">
            <span className="status-icon">📌</span>
            <span className="status-text">IPFS Ready — Media stored on Pinata</span>
          </div>
        ) : (
          <div className="status-indicator disconnected">
            <span className="status-icon">⚠️</span>
            <span className="status-text">Pinata not configured — media uploads disabled</span>
          </div>
        )}
      </div>

      {/* Video duration error */}
      {videoDurationError && (
        <div className="video-duration-error">
          ⏱️ {videoDurationError}
        </div>
      )}

      {/* File Input */}
      <div className="file-input-container">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
          multiple={maxFiles > 1}
          onChange={handleFileSelect}
          disabled={disabled || uploading || !isReady}
          className="file-input"
          id="media-upload-input"
        />
        <label
          htmlFor="media-upload-input"
          className={`file-input-label ${disabled || uploading || !isReady ? 'disabled' : ''}`}
        >
          <span className="upload-icon">📎</span>
          <span className="upload-text">
            {selectedFiles.length > 0
              ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected`
              : `Add media (video max ${MAX_VIDEO_DURATION}s)`
            }
          </span>
        </label>
      </div>

      {/* File Previews */}
      {showPreview && previews.length > 0 && (
        <div className="media-previews">
          {previews.map((preview, index) => (
            <div key={index} className="media-preview">
              {preview.type === 'video' ? (
                <video src={preview.url} controls className="preview-video" muted />
              ) : (
                <img src={preview.url} alt={`Preview ${index + 1}`} className="preview-image" />
              )}
              <div className="preview-overlay">
                <button className="remove-btn" onClick={() => removeFile(index)} disabled={uploading}>✕</button>
                <div className="file-info">
                  <span className="file-name">{preview.file.name}</span>
                  <span className="file-size">{(preview.file.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Controls */}
      {selectedFiles.length > 0 && (
        <div className="upload-controls">
          <button
            className="upload-btn"
            onClick={uploadFiles}
            disabled={uploading || selectedFiles.length === 0 || !isReady}
          >
            {uploading ? (
              <><span className="upload-spinner">⏳</span> Uploading to IPFS... {uploadProgress}%</>
            ) : (
              <><span className="upload-icon">📌</span> Upload to IPFS</>
            )}
          </button>
          <button className="clear-btn" onClick={clearAll} disabled={uploading}>Clear All</button>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
          <span className="progress-text">{uploadProgress}% uploaded</span>
        </div>
      )}

      {/* Storage Upgrade Modal */}
      {showUpgradeModal && upgradeInfo && (
        <div className="upgrade-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="upgrade-modal" onClick={e => e.stopPropagation()}>
            <h3>📦 Upgrade Storage</h3>
            <p className="upgrade-current">
              Current: <strong>{upgradeInfo.storageInfo?.tierName || 'Free'}</strong> — {formatBytes(upgradeInfo.storageInfo?.used || 0)} / {upgradeInfo.storageInfo?.tierLabel || '50 MB'}
            </p>
            <div className="upgrade-tiers">
              {(upgradeInfo.upgradeTiers || []).map(tier => (
                <div key={tier.key} className="upgrade-tier-card">
                  <div className="tier-name">{tier.name}</div>
                  <div className="tier-limit">{tier.label}</div>
                  <div className="tier-cost">{tier.costSol} SOL</div>
                  <button
                    className="tier-buy-btn"
                    onClick={async () => {
                      // Payment requires wallet adapter — emit event for parent to handle
                      window.dispatchEvent(new CustomEvent('znoospace_storage_upgrade', {
                        detail: { tierKey: tier.key, costSol: tier.costSol }
                      }));
                      setShowUpgradeModal(false);
                    }}
                  >
                    Upgrade for {tier.costSol} SOL
                  </button>
                </div>
              ))}
            </div>
            <button className="upgrade-close" onClick={() => setShowUpgradeModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
